import { ButtonHierarchy, MainButtonProps } from "@beydesign/storybook"
import {
  BillingInterval,
  PlanUpdateStatus,
  StripePrice,
  StripeProduct,
  StripeProducts,
} from "../types"
import { getCurrencySymbol } from "./currency"

const EUROPEAN_TIMEZONES = new Set([
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Vienna",
  "Europe/Zurich",
  "Europe/Stockholm",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Warsaw",
  "Europe/Prague",
  "Europe/Budapest",
  "Europe/Moscow",
  "Europe/Istanbul",
  "Europe/London",
])

const EUROPEAN_LOCALES = new Set([
  "fr-FR",
  "de-DE",
  "es-ES",
  "it-IT",
  "nl-NL",
  "pt-PT",
  "pl-PL",
  "sv-SE",
  "da-DK",
  "nb-NO",
  "fi-FI",
  "cs-CZ",
  "hu-HU",
  "ro-RO",
  "bg-BG",
  "hr-HR",
  "sk-SK",
  "sl-SI",
  "et-EE",
  "lv-LV",
  "lt-LT",
  "en-GB",
  "en-IE",
  "ru-RU",
  "tr-TR",
])

export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    console.warn("Failed to detect timezone:", error)
    return "America/New_York" // Default fallback
  }
}

export function getUserLocale(): string {
  try {
    return navigator.language || navigator.languages?.[0] || "en-US"
  } catch (error) {
    console.warn("Failed to detect locale:", error)
    return "en-US" // Default fallback
  }
}

/**
 * Gets the appropriate currency for the user based on timezone and locale
 * Returns EUR for European regions, USD for rest of world
 */
export function getRegionalCurrency(): string {
  const timezone = getUserTimezone()
  const locale = getUserLocale()

  if (EUROPEAN_TIMEZONES.has(timezone)) {
    return "eur"
  }

  if (EUROPEAN_LOCALES.has(locale)) {
    return "eur"
  }

  return "usd"
}

export function getRegionalPrice(price: StripePrice): {
  currency: string
  unitAmount: number
  unitAmountDecimal: string
} {
  const regionalCurrency = getRegionalCurrency()

  const regionalPricing = price.currency_options?.[regionalCurrency]

  if (regionalPricing) {
    return {
      currency: regionalCurrency,
      unitAmount: regionalPricing.unit_amount,
      unitAmountDecimal: regionalPricing.unit_amount_decimal,
    }
  }

  return {
    currency: price.currency,
    unitAmount: price.unit_amount,
    unitAmountDecimal: (price.unit_amount / 100).toFixed(2),
  }
}

export function formatPriceAmount(unitAmount: number): number {
  return unitAmount / 100
}

/**
 * Finds a price matching the specified billing interval with interval_count of 1
 * @param prices - Array of stripe prices
 * @param selectedInterval - The desired billing interval (month or year)
 * @returns The matching price or undefined if no price is found
 */
export function findPriceByInterval(
  prices: StripePrice[] | undefined,
  selectedInterval: BillingInterval,
): StripePrice | undefined {
  if (!prices || prices.length === 0) return undefined

  const matchingPrice = prices.find(
    (price) =>
      price.recurring &&
      price.recurring.interval === selectedInterval &&
      price.recurring.interval_count === 1,
  )

  if (!matchingPrice) {
    console.warn(
      `No price found for interval "${selectedInterval}" with interval_count=1`,
      { availablePrices: prices },
    )
  }

  return matchingPrice
}

/**
 * Calculates the monthly price amount from regional pricing
 * For yearly prices, divides by 12 and rounds
 * @param regionalPrice - The regional pricing object
 * @param interval - The billing interval
 * @returns The monthly price amount in cents, or null if no regional price
 */
export function calculatePricePerMonth(
  regionalPrice: { unitAmount: number } | null,
  interval: BillingInterval | undefined,
): number | null {
  if (!regionalPrice) return null

  if (interval === BillingInterval.YEAR) {
    return Math.round(regionalPrice.unitAmount / 12)
  }

  return regionalPrice.unitAmount
}

export function getPaymentButtonProps(
  sortedProducts: StripeProduct[],
  currentProduct: StripeProduct,
  selectedInterval: BillingInterval,
): MainButtonProps {
  const currentProductIndex = sortedProducts.findIndex(
    (product) => product.id === currentProduct.id,
  )

  const isCurrentPlanActive = currentProduct.activePlan

  if (checkIfPlanIsEnterprise(currentProduct)) {
    if (isCurrentPlanActive) {
      return { text: "Current Plan", disabled: true }
    }
    return { text: "Schedule a Call", disabled: false }
  }

  const hasMonthlyPrice = currentProduct.prices?.some(
    (price) =>
      price.recurring?.interval === BillingInterval.MONTH &&
      price.recurring?.interval_count === 1,
  )
  const hasYearlyPrice = currentProduct.prices?.some(
    (price) =>
      price.recurring?.interval === BillingInterval.YEAR &&
      price.recurring?.interval_count === 1,
  )

  const currentPlanActivePrice = currentProduct.prices?.find(
    (price) => price.activePrice,
  )
  const isCurrentIntervalActive =
    currentPlanActivePrice?.recurring?.interval === selectedInterval &&
    currentPlanActivePrice?.recurring?.interval_count === 1

  if (isCurrentPlanActive) {
    if (hasMonthlyPrice && hasYearlyPrice) {
      if (isCurrentIntervalActive) {
        return {
          text:
            selectedInterval === BillingInterval.MONTH
              ? "Monthly Plan active"
              : "Yearly Plan active",
          disabled: true,
        }
      } else {
        if (
          currentPlanActivePrice?.recurring?.interval === BillingInterval.YEAR
        ) {
          return {
            text: "Downgrade",
            disabled: false,
            hierarchy: ButtonHierarchy.Secondary,
          }
        } else {
          return { text: "Switch to yearly", disabled: false }
        }
      }
    } else {
      return { text: "Current Plan", disabled: true }
    }
  } else {
    const activeProductIndex = sortedProducts.findIndex(
      (product) => product.activePlan,
    )

    if (activeProductIndex === -1 || currentProductIndex === -1) {
      return { text: "Upgrade", disabled: false }
    }

    if (currentProductIndex > activeProductIndex) {
      return { text: "Upgrade", disabled: false }
    } else {
      return {
        text: "Downgrade",
        disabled: false,
        hierarchy: ButtonHierarchy.Secondary,
      }
    }
  }
}

export function checkIfPlanIsMostPopular(currentProduct: StripeProduct) {
  return currentProduct.metadata?.most_popular?.toLowerCase() === "true"
}

export function checkIfPlanIsEnterprise(currentProduct: StripeProduct) {
  return currentProduct.name.toLowerCase().includes("enterprise")
}

type PlanBubbleData = { text: string; description: string } | null
export function getPlanBubbleData(
  currentProduct: StripeProduct,
  selectedInterval: BillingInterval,
): PlanBubbleData {
  if (!currentProduct.activePlan) return null

  const hasMonthlyPrice = currentProduct.prices?.some(
    (price) =>
      price.recurring?.interval === BillingInterval.MONTH &&
      price.recurring?.interval_count === 1,
  )
  const hasYearlyPrice = currentProduct.prices?.some(
    (price) =>
      price.recurring?.interval === BillingInterval.YEAR &&
      price.recurring?.interval_count === 1,
  )

  if (!hasMonthlyPrice || !hasYearlyPrice) return null

  const currentPlanActivePrice = currentProduct.prices?.find(
    (price) => price.activePrice,
  )

  if (!currentPlanActivePrice) return null

  const activeInterval = currentPlanActivePrice.recurring?.interval

  if (
    selectedInterval === BillingInterval.MONTH &&
    activeInterval === BillingInterval.YEAR
  ) {
    return {
      text: "You're currently on the yearly plan",
      description: "You can switch to monthly after your current period ends.",
    }
  }

  if (
    selectedInterval === BillingInterval.YEAR &&
    activeInterval === BillingInterval.MONTH
  ) {
    return {
      text: "You're currently on the monthly plan",
      description: "You can switch to yearly after your current period ends.",
    }
  }

  return null
}

export function getPlanUpdateStatus(
  newPlanId: string,
  selectedInterval: BillingInterval,
  sortedProducts: StripeProduct[],
): PlanUpdateStatus {
  const currentActivePlan = sortedProducts.find((p) => p.activePlan)
  const currentActivePlanIndex = sortedProducts.findIndex((p) => p.activePlan)
  const newPlanIndex = sortedProducts.findIndex((p) => p.id === newPlanId)

  if (
    !currentActivePlan ||
    currentActivePlanIndex === -1 ||
    newPlanIndex === -1
  ) {
    return null
  }

  if (currentActivePlan.id === newPlanId) {
    const currentActivePrice = currentActivePlan.prices?.find(
      (price) => price.activePrice,
    )
    const currentInterval = currentActivePrice?.recurring?.interval

    if (currentInterval && currentInterval !== selectedInterval) {
      if (
        currentInterval === BillingInterval.MONTH &&
        selectedInterval === BillingInterval.YEAR
      ) {
        return "upgraded"
      }
      if (
        currentInterval === BillingInterval.YEAR &&
        selectedInterval === BillingInterval.MONTH
      ) {
        return "downgraded"
      }
    }
    return null
  }
  return newPlanIndex > currentActivePlanIndex ? "upgraded" : "downgraded"
}

export function getCurrentPlanPrice(
  stripeProducts: StripeProducts,
  userSubscriptionPlan?: string,
): string | null {
  if (Object.keys(stripeProducts).length === 0) return null

  if (userSubscriptionPlan?.toLowerCase().includes("enterprise")) {
    return null
  }

  const activeProduct = Object.values(stripeProducts).find(
    (product) => product.activePlan,
  )
  if (!activeProduct || !activeProduct.prices) return null

  const activePrice = activeProduct.prices.find(
    (price) => price.activePrice && price.recurring?.interval_count === 1,
  )
  if (!activePrice || !activePrice.recurring) return null
  const interval = activePrice.recurring.interval
  const regionalPrice = getRegionalPrice(activePrice)
  const formattedPrice = formatPriceAmount(regionalPrice.unitAmount)
  return `${getCurrencySymbol(regionalPrice.currency)}${formattedPrice.toFixed(0)}`
}
